import { useEffect, useState } from "react";
import { Link2, Loader2, Unplug, KeyRound, RefreshCw, Radar } from "lucide-react";
import { toast } from "sonner";
import { Panel, PageHeader, Tag } from "@/components/Panel";
import { PendingCard } from "@/components/PendingCard";
import { OverduePanel } from "@/components/OverduePanel";
import { http, useResource, formatError, money } from "@/lib/api";

const Connection = ({ st, reload }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatError(e)); } setBusy(false); reload(); };
  const connect = () => run(async () => {
    const { data } = await http.post("/omvexis/connect");
    toast.success(data.otp_required ? `Code sent to ${data.sent_to}` : "Omvexis linked");
  });
  const verify = () => run(async () => { await http.post("/omvexis/otp", { code }); setCode(""); toast.success("Omvexis admin portal linked"); });
  const disconnect = () => run(() => http.post("/omvexis/disconnect"));
  return (
    <Panel label="Link Status" title={st?.connected ? `Linked as ${st.user?.name}` : "Not linked"} testId="omvexis-connection"
      right={<span className={`text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full border ${st?.connected ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`} data-testid="omvexis-status-badge">{st?.connected ? "online" : st?.awaiting_otp ? "awaiting code" : "offline"}</span>}>
      {st?.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-400 font-mono-hud">{st.user?.email} · {st.user?.role}</span>
          <button className="hud-btn hud-btn-danger !py-1.5" onClick={disconnect} disabled={busy} data-testid="omvexis-disconnect-button"><Unplug size={13} /> Unlink</button>
        </div>
      ) : st?.awaiting_otp ? (
        <div className="flex gap-2 max-w-md">
          <input className="hud-input font-mono-hud tracking-[0.4em]" placeholder="Login code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="omvexis-otp-input" />
          <button className="hud-btn shrink-0" onClick={verify} disabled={busy || !code} data-testid="omvexis-otp-submit">{busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Verify</button>
          <button className="hud-btn hud-btn-ghost shrink-0" onClick={connect} disabled={busy} data-testid="omvexis-resend-button">Resend</button>
        </div>
      ) : (
        <button className="hud-btn" onClick={connect} disabled={busy} data-testid="omvexis-connect-button">{busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Connect admin portal</button>
      )}
    </Panel>
  );
};

const useOmx = (path, enabled) => {
  const [data, setData] = useState(null);
  const load = () => enabled && http.get("/omvexis/data", { params: { path } }).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [enabled, path]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
};

const Stat = ({ label, value, testId }) => (
  <div className="hud-panel p-5" data-testid={testId}><div className="hud-label">{label}</div><div className="font-display text-3xl font-bold mt-2">{value ?? "—"}</div></div>
);

const Th = ({ cols }) => <thead><tr className="border-b border-cyan-500/30">{cols.map((h) => <th key={h} className="hud-label py-2 px-3 font-normal">{h}</th>)}</tr></thead>;

export default function Omvexis() {
  const [st, reloadSt] = useResource("/omvexis/status");
  const [pending, reloadPending] = useResource("/omvexis/pending");
  const [scanning, setScanning] = useState(false);
  const scan = async () => {
    setScanning(true);
    try { const { data } = await http.post("/omvexis/autopilot/run"); toast.success(`${data.queued} new payment-due notice(s) queued`); reloadPending(); }
    catch (e) { toast.error(formatError(e)); }
    setScanning(false);
  };
  const on = !!st?.connected;
  const [stats, r1] = useOmx("/stats", on);
  const [ships, r2] = useOmx("/shipments", on);
  const [buyers, r3] = useOmx("/buyers", on);
  const refresh = () => { r1(); r2(); r3(); };
  return (
    <div>
      <PageHeader code="LINK://OMVEXIS.IN" title="Omvexis Portal" sub="Live view of your export portal. Ask JARVIS anything — 'notify all overdue buyers', 'add a buyer', 'approve pending users' — from the Command console."
        right={on && <div className="flex gap-2">
          <button className="hud-btn" onClick={scan} disabled={scanning} data-testid="omvexis-autopilot-scan">{scanning ? <Loader2 size={13} className="animate-spin" /> : <Radar size={13} />} Scan overdue now</button>
          <button className="hud-btn hud-btn-ghost" onClick={refresh} data-testid="omvexis-refresh-button"><RefreshCw size={13} /> Refresh</button>
        </div>} />
      <div className="space-y-6">
        <Connection st={st} reload={reloadSt} />
        {pending?.length > 0 && (
          <Panel label="Awaiting your confirmation" title={`${pending.length} pending change(s)`} testId="omvexis-pending-panel">
            <div className="space-y-2">{pending.map((p) => <PendingCard key={p.id} item={p} />)}</div>
          </Panel>
        )}
        {on && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Stat label="Shipments" value={stats?.shipments} testId="omx-stat-shipments" />
              <Stat label="Overdue" value={stats?.overdue} testId="omx-stat-overdue" />
              <Stat label="Awaiting Contract" value={stats?.awaiting_contract} testId="omx-stat-contract" />
              <Stat label="Paid" value={stats?.paid} testId="omx-stat-paid" />
              <Stat label="Paid USD" value={stats && money(stats.paid_usd)} testId="omx-stat-paid-usd" />
            </div>
            <OverduePanel />
            <Panel label="Receivables" title="Outstanding by Buyer" testId="omx-outstanding">
              <table className="w-full text-left"><Th cols={["Buyer", "Country", "Open", "Invoiced", "Paid", "Outstanding", "Overdue"]} />
                <tbody>{(stats?.outstanding_by_buyer || []).map((b) => (
                  <tr key={b.buyer_id + b.exporter_id} className="border-b border-cyan-900/50 hover:bg-cyan-950/30">
                    <td className="py-2 px-3 text-slate-100">{b.buyer_name}</td><td className="px-3 text-slate-400">{b.country}</td><td className="px-3">{b.open_invoices}</td>
                    <td className="px-3">{money(b.invoiced)}</td><td className="px-3 text-emerald-300">{money(b.paid)}</td><td className="px-3 text-cyan-200 font-display">{money(b.outstanding)}</td><td className="px-3 text-red-300">{b.overdue}</td>
                  </tr>))}</tbody></table>
            </Panel>
            <Panel label="Live" title="Shipments" testId="omx-shipments">
              <div className="overflow-x-auto"><table className="w-full text-left"><Th cols={["Ref", "Product", "Buyer", "Amount", "Due", "Payment", "Docs"]} />
                <tbody>{(ships || []).map((s) => (
                  <tr key={s.id} className="border-b border-cyan-900/50 hover:bg-cyan-950/30" data-testid={`omx-shipment-${s.reference}`}>
                    <td className="py-2 px-3 font-mono-hud text-[12px] text-cyan-300">{s.reference}</td><td className="px-3 text-slate-200">{s.product}</td><td className="px-3 text-slate-400">{s.buyer?.company_name}</td>
                    <td className="px-3">{money(s.net_buyer_amount ?? s.buyer_amount)}</td><td className="px-3 font-mono-hud text-[12px]">{s.payment_due_date || "—"}</td>
                    <td className="px-3"><Tag value={s.payment_state || "—"} /></td><td className="px-3 text-slate-400 text-sm">{s.doc_types_uploaded?.length || 0} / 10</td>
                  </tr>))}</tbody></table></div>
            </Panel>
            <Panel label="Clients" title="Buyers" testId="omx-buyers">
              <table className="w-full text-left"><Th cols={["Company", "Contact", "Email", "Country", "Shipments", "Due"]} />
                <tbody>{(buyers || []).map((b) => (
                  <tr key={b.id} className="border-b border-cyan-900/50 hover:bg-cyan-950/30">
                    <td className="py-2 px-3 text-slate-100">{b.company_name}</td><td className="px-3 text-slate-400">{b.contact_person}</td><td className="px-3 font-mono-hud text-[12px] text-slate-400">{b.email}</td>
                    <td className="px-3">{b.country}</td><td className="px-3">{b.shipment_count}</td><td className="px-3 text-red-300">{b.due_count}</td>
                  </tr>))}</tbody></table>
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}
